"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CalloutElement = void 0;
const tslib_1 = require("tslib");
const React = tslib_1.__importStar(require("react"));
const nano_theme_1 = require("nano-theme");
const slate_react_1 = require("slate-react");
const slate_1 = require("slate");
const MoveToViewport_1 = require("@jsonjoy.com/ui/lib/utils/popup/MoveToViewport");
const PopupControlled_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/PopupControlled");
const Scrollbox_1 = require("@jsonjoy.com/ui/lib/4-card/Scrollbox");
const context_1 = require("@jsonjoy.com/ui/lib/4-card/Popup/context");
const popup_1 = require("@jsonjoy.com/ui/lib/utils/popup");
const useLockScrolling_1 = require("@jsonjoy.com/ui/lib/hooks/useLockScrolling");
const useSingletonPopup_1 = require("@jsonjoy.com/ui/lib/hooks/useSingletonPopup");
const context_2 = require("@jsonjoy.com/ui/lib/styles/context");
const flexible_input_1 = require("flexible-input");
const use_t_1 = require("use-t");
const BlockPlaceholder_1 = require("../../components/blocks/BlockPlaceholder");
const StripBarHandle_1 = require("../../components/blocks/StripBarHandle");
const indentation_1 = require("../../behavior/indentation");
const font_1 = require("../../behavior/font");
const css_1 = require("../../custom-style/css");
const util_1 = require("../../util");
const CalloutOptions_1 = require("./CalloutOptions");
const colors_1 = require("./colors");
const settings_1 = require("./settings");
const INDICATOR_SIZE = 28;
const calloutClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '24px 0',
    pd: '16px',
    bdrad: '8px',
    d: 'flex',
    fld: 'column',
    gap: '6px',
    bg: 'var(--cb-bg)',
    bd: '1px solid var(--cb-bd)',
    trs: 'background-color .2s ease, border-color .2s ease',
    '&:hover': {
        bg: 'var(--cb-bg-hover)',
        borderColor: 'var(--cb-bd-hover)',
    },
    [`&:hover .${StripBarHandle_1.stripBarHandleTriggerClass}`]: {
        opacity: 1,
        pointerEvents: 'auto',
    },
    [`&:hover .${StripBarHandle_1.stripBarHandleTriggerClass} .${StripBarHandle_1.stripBarHandleFillClass}`]: {
        width: '100%',
    },
});
const handleClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    insetInlineStart: '-3px',
    t: '8px',
    b: '8px',
    w: '5px',
    bdrad: '2px',
    pe: 'none',
});
const handleButtonClass = (0, nano_theme_1.rule)({
    pos: 'absolute',
    insetInlineStart: '-3px',
    t: '8px',
    b: '8px',
    w: '5px',
    bdrad: '2px',
    pad: 0,
    bd: 'none',
    cur: 'pointer',
    out: 'none',
    trs: 'width .1s ease, inset-inline-start .1s ease, transform .1s ease',
    '&:hover': {
        w: '7px',
        insetInlineStart: '-4px',
    },
    '&:active': {
        transform: 'scaleY(0.97)',
    },
});
const handleButtonContentClass = (0, nano_theme_1.rule)({
    d: 'block',
    w: '5px',
    h: '100%',
    pad: 0,
    bd: 'none',
    bdrad: '2px',
    cur: 'pointer',
    out: 'none',
    trs: 'width .1s ease, transform .1s ease',
    '&:hover': { w: '7px' },
    '&:active': { transform: 'scaleY(0.97)' },
});
const headerClass = (0, nano_theme_1.rule)({
    d: 'flex',
    ai: 'center',
    gap: '8px',
    us: 'none',
    minH: `${INDICATOR_SIZE}px`,
});
const indicatorTriggerClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    w: `${INDICATOR_SIZE}px`,
    h: `${INDICATOR_SIZE}px`,
    bdrad: '6px',
    pad: 0,
    bd: 'none',
    bg: 'transparent',
    cur: 'pointer',
    lh: 1,
    flexShrink: 0,
    trs: 'background-color .08s ease, transform .08s ease',
    '&:hover': {
        transform: 'scale(1.03)',
    },
    '&:active': {
        transform: 'scale(0.97)',
    },
});
const indicatorGlyphClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    lh: 1,
});
const indicatorTextClass = (0, nano_theme_1.rule)({
    ...nano_theme_1.font.slab.bold,
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    fz: '0.95rem',
    lh: 1,
    ['textBoxTrim']: 'trim-both',
    ['textBoxEdge']: 'cap alphabetic',
});
const indicatorEmojiClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    jc: 'center',
    fz: '1.2rem',
    lh: 1,
});
const EMOJI_RE = /\p{Extended_Pictographic}/u;
const isEmoji = (s) => EMOJI_RE.test(s);
const titleClass = (0, nano_theme_1.rule)({
    ...nano_theme_1.font.ui3.mid,
    textTransform: '',
    d: 'inline-flex',
    ai: 'center',
    h: '100%',
    fz: '0.95rem',
    lh: 1.3,
    flex: '1',
    minW: '0',
    ['textBoxTrim']: 'trim-both',
    ['textBoxEdge']: 'cap alphabetic',
    marginInlineStart: '-4px',
});
const titleStripSlotClass = (0, nano_theme_1.rule)({
    d: 'inline-flex',
    ai: 'center',
    flex: '1',
    minW: 0,
    marginInlineStart: '-4px',
});
const bodyClass = (0, nano_theme_1.rule)({
    pos: 'relative',
    mr: '0',
    fz: '0.98em',
    lh: 1.6,
});
const bindVoidInputKeyDown = (el, editor, onCancel) => {
    if (!el)
        return () => { };
    const handler = (evt) => {
        const e = evt;
        e.stopPropagation();
        if (e.key === 'Enter' || e.key === 'Tab') {
            e.preventDefault();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel?.();
            e.target.blur();
            requestAnimationFrame(() => slate_react_1.ReactEditor.focus(editor));
        }
    };
    el.addEventListener('keydown', handler);
    return () => el.removeEventListener('keydown', handler);
};
const CalloutElement = ({ attributes, children, element }) => {
    const editor = (0, slate_react_1.useSlateStatic)();
    const styles = (0, context_2.useStyles)();
    const readOnly = (0, slate_react_1.useReadOnly)();
    const [t] = (0, use_t_1.useT)();
    const variant = (0, settings_1.getCalloutVariant)(element.variant);
    const accent = element.color?.trim() || (0, settings_1.getVariantAccent)(styles, variant);
    const icon = element.icon ?? '';
    const variantTitle = settings_1.VARIANT_TITLE[variant];
    const titleOverride = element.title;
    const resolvedTitle = titleOverride ?? variantTitle;
    const colors = React.useMemo(() => (0, colors_1.getCalloutColors)(styles, accent), [styles, accent]);
    const [editingTitle, setEditingTitle] = React.useState(false);
    const [titleDraft, setTitleDraft] = React.useState(resolvedTitle);
    const titleFocusedRef = React.useRef(false);
    React.useEffect(() => {
        if (!titleFocusedRef.current)
            setTitleDraft(resolvedTitle);
    }, [resolvedTitle]);
    const commitTitle = React.useCallback(() => {
        titleFocusedRef.current = false;
        const next = titleDraft;
        try {
            const path = slate_react_1.ReactEditor.findPath(editor, element);
            if (!next) {
                // Empty: unset if there was an override (back to auto); no-op if
                // already auto.
                if (titleOverride !== undefined)
                    slate_1.Transforms.unsetNodes(editor, 'title', { at: path });
            }
            else if (next === variantTitle) {
                // Typing the variant default is equivalent to "use the default" —
                // unset to keep storage clean, regardless of prior state.
                if (titleOverride !== undefined)
                    slate_1.Transforms.unsetNodes(editor, 'title', { at: path });
            }
            else {
                slate_1.Transforms.setNodes(editor, { title: next }, { at: path });
            }
        }
        catch { }
        if (!next)
            setEditingTitle(false);
    }, [editor, element, titleDraft, titleOverride, variantTitle]);
    const [titleInputEl, setTitleInputEl] = React.useState(null);
    React.useEffect(() => {
        if (readOnly)
            return;
        return bindVoidInputKeyDown(titleInputEl, editor, () => {
            setTitleDraft(resolvedTitle);
            titleFocusedRef.current = false;
            if (!resolvedTitle)
                setEditingTitle(false);
        });
    }, [titleInputEl, readOnly, resolvedTitle, editor]);
    // Options popup.
    const popup = (0, useSingletonPopup_1.useSingletonPopup)('callout-options');
    const closePopup = React.useCallback(() => popup.setOpen(false), [popup]);
    const popupContextValue = React.useMemo(() => ({ close: closePopup }), [closePopup]);
    const popupAnchorHandle = (0, popup_1.useAnchorPointHandle)({
        horizontal: true,
        center: true,
        pinX: 'left',
        minSpace: 360,
        gap: 8,
    });
    (0, useLockScrolling_1.useLockScrolling)(popup.open);
    const preventMouseDown = React.useCallback((event) => {
        event.preventDefault();
        event.stopPropagation();
    }, []);
    const VariantIcon = settings_1.VARIANT_ICONS[variant];
    const triggerBg = colors.bg;
    const triggerBgHover = styles.light ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.06)';
    const hideHeader = !!element.hideHeader;
    const hasIconOverride = element.icon !== undefined;
    const iconIsEmoji = hasIconOverride && !!icon && isEmoji(icon);
    const indicator = !hasIconOverride ? (React.createElement("span", { className: indicatorGlyphClass, style: { color: colors.title } },
        React.createElement(VariantIcon, { style: { color: colors.title } }))) : icon ? (React.createElement("span", { className: iconIsEmoji ? indicatorEmojiClass : indicatorTextClass, style: { color: colors.title } }, icon)) : null;
    const isExplicitBlank = titleOverride === '';
    const showTitleStrip = !readOnly && isExplicitBlank && !editingTitle;
    const showTitleField = !isExplicitBlank || editingTitle;
    return (React.createElement("aside", { ...attributes, className: calloutClass, style: {
            textAlign: element.align,
            marginInlineStart: (0, indentation_1.indentPadding)(element.indent) ?? 0,
            marginInlineEnd: 0,
            fontFamily: (0, font_1.fontFamilyOf)(element.font),
            color: (0, css_1.fgVar)(18, styles.g(0.18)),
            boxShadow: `0 1px 4px ${colors.shadow}`,
            ['--cb-bg']: colors.bg,
            ['--cb-bg-hover']: colors.bgHover,
            ['--cb-bd']: colors.bd,
            ['--cb-bd-hover']: colors.bdHover,
            ['--mutxt-handle-color']: styles.g(0.72),
            ['--mutxt-handle-color-hover']: styles.g(0.35),
        } },
        readOnly ? (React.createElement("span", { "aria-hidden": "true", contentEditable: false, className: handleClass, style: { background: colors.accent } })) : hideHeader ? (
        // Header is gone — the handle is the *only* affordance, so it
        // anchors the popup itself. PopupControlled's outer span carries the
        // absolute positioning; the inner button fills it.
        React.createElement(context_1.context.Provider, { value: popupContextValue },
            React.createElement(popup_1.anchorContext.Provider, { value: popupAnchorHandle },
                React.createElement(PopupControlled_1.PopupControlled, { open: popup.open, refToggle: popupAnchorHandle.ref, onHeadClick: () => popup.setOpen(!popup.open), onClickAway: closePopup, onEsc: popup.open ? closePopup : undefined, style: {
                        position: 'absolute',
                        insetInlineStart: '-3px',
                        top: '8px',
                        bottom: '8px',
                    }, renderContext: () => (React.createElement(MoveToViewport_1.MoveToViewport, { vertical: true },
                        React.createElement("div", { style: {
                                display: 'flex',
                                flexDirection: 'column',
                                maxHeight: 'calc(100vh - 16px)',
                            } },
                            React.createElement(Scrollbox_1.Scrollbox, { shadow: true },
                                React.createElement("div", { style: { padding: 16 } },
                                    React.createElement(CalloutOptions_1.CalloutOptions, { element: element })))))) },
                    React.createElement("button", { type: "button", contentEditable: false, className: handleButtonContentClass, "aria-label": t('Callout options'), "aria-haspopup": "dialog", "aria-expanded": popup.open, onMouseDown: preventMouseDown, style: { background: colors.accent } }))))) : (
        // Header is visible — the icon trigger is the popup anchor; the
        // handle is a secondary trigger that toggles the same singleton
        // popup.
        React.createElement("button", { type: "button", contentEditable: false, className: handleButtonClass, "aria-label": t('Callout options'), "aria-haspopup": "dialog", "aria-expanded": popup.open, onMouseDown: preventMouseDown, onClick: () => popup.setOpen(!popup.open), style: { background: colors.accent } })),
        !hideHeader && (React.createElement("div", { contentEditable: false, className: headerClass },
            readOnly ? (React.createElement("span", { className: indicatorTriggerClass, style: { background: triggerBg } }, indicator)) : (React.createElement(context_1.context.Provider, { value: popupContextValue },
                React.createElement(popup_1.anchorContext.Provider, { value: popupAnchorHandle },
                    React.createElement(PopupControlled_1.PopupControlled, { open: popup.open, refToggle: popupAnchorHandle.ref, onHeadClick: () => popup.setOpen(!popup.open), onClickAway: closePopup, onEsc: popup.open ? closePopup : undefined, renderContext: () => (React.createElement(MoveToViewport_1.MoveToViewport, { vertical: true },
                            React.createElement("div", { style: {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxHeight: 'calc(100vh - 16px)',
                                } },
                                React.createElement(Scrollbox_1.Scrollbox, { shadow: true },
                                    React.createElement("div", { style: { padding: 16 } },
                                        React.createElement(CalloutOptions_1.CalloutOptions, { element: element })))))) },
                        React.createElement("button", { type: "button", className: indicatorTriggerClass, "aria-label": t('Callout options'), "aria-haspopup": "dialog", "aria-expanded": popup.open, onMouseDown: preventMouseDown, style: { background: triggerBg }, onMouseEnter: (e) => {
                                e.currentTarget.style.background = triggerBgHover;
                            }, onMouseLeave: (e) => {
                                e.currentTarget.style.background = triggerBg;
                            } }, indicator))))),
            showTitleField ? (React.createElement("span", { className: titleClass, style: { color: colors.title } }, readOnly ? (resolvedTitle) : (React.createElement(flexible_input_1.FlexibleInput, { inp: setTitleInputEl, value: titleDraft, minWidth: 80, focus: editingTitle && !titleDraft, typeahead: titleDraft ? '' : t('Add title…'), onChange: (e) => setTitleDraft(e.target.value), onFocus: () => {
                    titleFocusedRef.current = true;
                }, onBlur: commitTitle })))) : showTitleStrip ? (React.createElement("span", { className: titleStripSlotClass },
                React.createElement(StripBarHandle_1.StripBarHandle, { tooltip: t('Add title'), ariaLabel: t('Add title'), onActivate: () => setEditingTitle(true) }))) : null)),
        React.createElement("div", { className: bodyClass },
            children,
            (0, util_1.isEmptyBlock)(element) && React.createElement(BlockPlaceholder_1.BlockPlaceholder, { element: element }))));
};
exports.CalloutElement = CalloutElement;
